import { useEffect, useState, type FormEvent } from 'react'
import {
  Plus,
  Trash2,
  Loader2,
  CircleCheck,
  CircleSlash,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../../components/ui/Modal'

interface ProjectRoleDto {
  id: string
  name: string
  description: string | null
  countsAsCAC: boolean
  position: number
  active: boolean
}

export default function ProjectRoles() {
  const { t } = useTranslation(['settings', 'common'])
  const [items, setItems] = useState<ProjectRoleDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<ProjectRoleDto | null>(null)

  const reload = async () => {
    setError(null)
    try {
      const path = includeInactive ? '/project-roles?includeInactive=true' : '/project-roles'
      const { items } = await api.get<{ items: ProjectRoleDto[] }>(path)
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projectRoles.errors.load'))
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive])

  const onDelete = async (r: ProjectRoleDto, evt: React.MouseEvent) => {
    evt.stopPropagation()
    if (
      !(await confirmDialog({
        message: t('projectRoles.confirmDelete', { name: r.name }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/project-roles/${r.id}`)
      await reload()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        toast.error(body?.error ?? err.message)
      } else {
        toast.error(err instanceof Error ? err.message : t('common:errors.generic'))
      }
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('projectRoles.title')}</h2>
          <p className="t-muted text-sm mt-1">
            {t('projectRoles.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 rounded accent-toga-500 focus:ring-toga-500/40"
            />
            {t('projectRoles.includeInactive')}
          </label>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga transition-all flex items-center gap-1.5"
          >
            <Plus size={16} /> {t('projectRoles.newButton')}
          </button>
        </div>
      </header>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider t-muted border-b border-base">
              <th className="px-4 py-3 font-medium">{t('projectRoles.columns.name')}</th>
              <th className="px-4 py-3 font-medium">{t('projectRoles.columns.description')}</th>
              <th className="px-4 py-3 font-medium">{t('projectRoles.columns.type')}</th>
              <th className="px-4 py-3 font-medium">{t('projectRoles.columns.order')}</th>
              <th className="px-4 py-3 font-medium">{t('projectRoles.columns.status')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('projectRoles.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> {t('projectRoles.loading')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center t-muted">
                  {t('projectRoles.empty')}
                </td>
              </tr>
            ) : (
              items.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setEditing(r)}
                  className="border-b border-base/50 last:border-b-0 hover:bg-hover/50 cursor-pointer"
                >
                  <td className="px-4 py-3 t-primary font-medium">{r.name}</td>
                  <td className="px-4 py-3 t-secondary text-xs max-w-md truncate">
                    {r.description ?? <span className="t-faint">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {r.countsAsCAC ? (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-orange-500/15 text-orange-700 dark:text-orange-200 border-orange-500/40">
                        {t('projectRoles.type.cac')}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/40">
                        {t('projectRoles.type.execution')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 t-secondary font-mono text-xs">{r.position}</td>
                  <td className="px-4 py-3">
                    {r.active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300">
                        <CircleCheck size={14} /> {t('projectRoles.status.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs t-muted">
                        <CircleSlash size={14} /> {t('projectRoles.status.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => onDelete(r, e)}
                      title={t('projectRoles.deleteTitle')}
                      className="w-8 h-8 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ProjectRoleModal
        open={showCreate}
        mode="create"
        onClose={() => setShowCreate(false)}
        onSaved={() => {
          setShowCreate(false)
          void reload()
        }}
      />

      <ProjectRoleModal
        open={editing !== null}
        mode="edit"
        initial={editing ?? undefined}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          void reload()
        }}
      />
    </div>
  )
}

interface ModalProps {
  open: boolean
  mode: 'create' | 'edit'
  initial?: ProjectRoleDto
  onClose: () => void
  onSaved: () => void
}

function ProjectRoleModal({ open, mode, initial, onClose, onSaved }: ModalProps) {
  const { t } = useTranslation(['settings', 'common'])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [countsAsCAC, setCountsAsCAC] = useState(false)
  const [position, setPosition] = useState<string>('0')
  const [active, setActive] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initial) {
      setName(initial.name)
      setDescription(initial.description ?? '')
      setCountsAsCAC(initial.countsAsCAC)
      setPosition(String(initial.position))
      setActive(initial.active)
    } else {
      setName('')
      setDescription('')
      setCountsAsCAC(false)
      setPosition('0')
      setActive(true)
    }
    setError(null)
  }, [open, mode, initial])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const positionNum = Number.parseInt(position, 10)
      const payload = {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        countsAsCAC,
        position: Number.isFinite(positionNum) ? positionNum : 0,
        ...(mode === 'edit' ? { active } : {}),
      }
      if (mode === 'create') {
        await api.post('/project-roles', payload)
      } else if (initial) {
        await api.patch(`/project-roles/${initial.id}`, payload)
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? err.message)
      } else {
        setError(err instanceof Error ? err.message : t('projectRoles.errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? t('projectRoles.modal.createTitle') : t('projectRoles.modal.editTitle')}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm t-secondary mb-1.5">{t('projectRoles.form.name')}</label>
          <input
            required
            minLength={2}
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('projectRoles.form.namePlaceholder')}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
          />
        </div>

        <div>
          <label className="block text-sm t-secondary mb-1.5">{t('projectRoles.form.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={t('projectRoles.form.descriptionPlaceholder')}
            className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60 resize-y"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm t-secondary mb-1.5">{t('projectRoles.form.order')}</label>
            <input
              type="number"
              min={0}
              step={1}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary font-mono focus:outline-none focus:ring-1 focus:ring-toga-500/40 focus:border-toga-500/60"
            />
            <p className="text-[11px] t-faint mt-1">{t('projectRoles.form.orderHint')}</p>
          </div>
          <div className="flex flex-col gap-2 pt-7">
            <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={countsAsCAC}
                onChange={(e) => setCountsAsCAC(e.target.checked)}
                className="w-4 h-4 rounded accent-toga-500 focus:ring-toga-500/40"
              />
              {t('projectRoles.form.countsAsCAC')}
            </label>
            {mode === 'edit' && (
              <label className="inline-flex items-center gap-2 text-sm t-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="w-4 h-4 rounded accent-toga-500 focus:ring-toga-500/40"
                />
                {t('projectRoles.form.active')}
              </label>
            )}
          </div>
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || name.trim().length < 2}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === 'create' ? t('projectRoles.form.createSubmit') : t('projectRoles.form.saveSubmit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
